import { GuidanceSessionType } from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import Animated, { FadeIn, LinearTransition } from 'react-native-reanimated';
import { GuideBlurb, isWeb, LinkIcon, MemberTip, YStack } from 'smoothie';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { SIDEBAR_CONTENT_MAX_WIDTH } from '../../../common/utils/homeUtils';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { useMembershipGuidanceSessionInfo } from '../../../guidanceSessions/hooks/useMembershipGuidanceSessionInfo';
import { BeforeMoneyMapSession } from '../../../onboarding/components/BeforeMoneyMapSession';
import { useOnboardingCompleteness } from '../../../onboarding/hooks/useOnboardingCompleteness';

export const OnboardingWidget = () => {
    const user = useAuthedUser();
    const { value: goyoEnabled } = useFeatureGate(FeatureFlags.ENABLE_GOYO);
    const { guidanceOnboardingOrder, getSessionDetails } =
        useMembershipGuidanceSessionInfo();
    const {
        isLoadingOnboardingCompleteness: isLoadingOnboardingProfileCompleteness,
    } = useOnboardingCompleteness();

    if (isLoadingOnboardingProfileCompleteness) {
        return null;
    }

    const shouldShowBeforeMoneyMapSession =
        user.memberJourneyLog?.bookedDiscoverySession &&
        !user.memberJourneyLog?.completedRoadmapSession;

    if (!shouldShowBeforeMoneyMapSession) {
        return null;
    }

    // Under GOYO we keep the "next up" checklist but drop the guide blurb and
    // member tip, matching the booked homepage design.
    if (goyoEnabled) {
        return (
            <Animated.View
                style={{ width: '100%' }}
                {...(isWeb
                    ? { entering: FadeIn.duration(500) }
                    : {
                          entering: FadeIn.duration(500),
                          layout: LinearTransition.duration(500),
                      })}
            >
                <BeforeMoneyMapSession shouldRenderHeader={false} />
            </Animated.View>
        );
    }

    const hasCompletedDiscoverySession =
        user.memberJourneyLog?.completedDiscoverySession ?? false;
    const guideImageSrc = user.membership?.guide?.profileImageUrl;
    const firstOnboardingSessionName = getSessionDetails(
        guidanceOnboardingOrder[0] ?? GuidanceSessionType.DISCOVERY,
    ).title;
    const blurbTitle = 'Before our first session...';
    const blurbMessage =
        'Wrap up your financial profile and finish linking any additional accounts. This helps me prepare your personalized Money Map.';

    return (
        <Animated.View
            style={{ width: '100%' }}
            {...(isWeb
                ? { entering: FadeIn.duration(500) }
                : {
                      entering: FadeIn.duration(500),
                      layout: LinearTransition.duration(500),
                  })}
        >
            <YStack
                gap={24}
                width="100%"
                $sm={{
                    maxWidth: SIDEBAR_CONTENT_MAX_WIDTH,
                    alignSelf: 'center',
                }}
            >
                <YStack gap={12}>
                    {!hasCompletedDiscoverySession && (
                        <GuideBlurb
                            guideImageSrc={guideImageSrc}
                            heading={blurbTitle}
                            message={blurbMessage}
                        />
                    )}
                    <BeforeMoneyMapSession
                        shouldRenderHeader={!!hasCompletedDiscoverySession}
                    />
                </YStack>
                <MemberTip
                    icon={LinkIcon}
                    text={`Fruitful Members who connect accounts before their ${firstOnboardingSessionName} session are 2x more likely to get their Money Map within 7 days.`}
                />
            </YStack>
        </Animated.View>
    );
};
